import { useEffect, useMemo, useCallback, useState, useRef } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import cn from 'clsx'
import _min from 'lodash/min'
import _max from 'lodash/max'
import _sum from 'lodash/sum'
import _map from 'lodash/map'
import _keys from 'lodash/keys'
import _mean from 'lodash/mean'
import _isNil from 'lodash/isNil'
import _isNull from 'lodash/isNull'
import _values from 'lodash/values'
import _filter from 'lodash/filter'
import _isUndefined from 'lodash/isUndefined'

import Switch from '../Switch/Switch'
import Dropdown from '..//Dropdown/Dropdown'
import ChartHeader from '../ChartHeader/ChartHeader'
import BarChartNew, { BarChartGranularity } from '../BarChartNew/BarChartNew'
import { generateChartCSV } from '../../csv'
import downloadCSV from '../../utils/download_csv'
import exportElementToPNG from '../../utils/export_element_to_png'
import exportElementToJPG from '../../utils/export_element_to_jpg'
import getEmissionsDataMerged from '../../utils/data/get_emissions_data_merged'
import setOrAdd from '../../utils/set_or_add'
import formatUINumber from '../../utils/format_ui_number'
import getEmissionsDataTotal from '../../utils/data/get_emissions_data_total'
import useTotalArea from '../../hooks/use_total_area'
import { getEmissionsYears } from '../../hooks/emissions/get_years'
import useFunctionMemo from '../../hooks/use_function_memo'

import { EmissionsUnit } from 'types/common'
import { type ChartDataPoint } from 'types/ui'
import { type EmissionsData, type Emissions } from 'types/data'
import { type DropdownOption } from 'components/Dropdown/types'

import { CLASS_NAME, DisplayMode, UNIT_OPTIONS } from './const'

import './style.scss'

interface YearDropdownOption {
  label: string
  value: number
}

const convertCO2TonsToUnit = (
  value: number,
  unit: EmissionsUnit,
  area: number
): number => {
  switch (unit) {
    case EmissionsUnit.TonsPerHa:
      return area === 0 ? 0 : value / area
    case EmissionsUnit.KTonsPerHa:
      return (area === 0 ? 0 : value / area) / 1000
    case EmissionsUnit.KTons:
      return value / 1000
    case EmissionsUnit.Tons:
    default:
      return value
  }
}

const convertMonthlyDataToYearly = (data: EmissionsData): EmissionsData => {
  const result: EmissionsData = {}

  _keys(data).forEach((key: string): void => {
    setOrAdd(result, key.substring(0, 4), data[key])
  })

  return result
}

const getInterventionStartYearOptions = (
  years: number[],
  endYear: number | null
): YearDropdownOption[] =>
  useMemo((): YearDropdownOption[] => {
    const minYear = _min(years)

    // prettier-ignore
    return _isNil(minYear)
      ? []
      : years.filter(
        (year: number) => year > minYear && (endYear === null || year < endYear)
      ).map((year: number): YearDropdownOption => ({
        label: `${year}`,
        value: year
      }))
  }, [years, endYear])

const getInterventionEndYearOptions = (
  years: number[],
  startYear: number | null
): YearDropdownOption[] =>
  useMemo(
    (): YearDropdownOption[] =>
      years
        .filter((year: number) => startYear === null || year >= startYear)
        .map(
          (year: number): YearDropdownOption => ({
            label: `${year}`,
            value: year
          })
        ),
    [years, startYear]
  )

// prettier-ignore
const getBaselinePeriodOptions = (
  years: number[],
  interventionStartYear: number | null
): number[] =>
  interventionStartYear === null
    ? []
    : _filter(
      _map(years, (year: number): number => interventionStartYear - year),
      (value: number): boolean => value > 0
    )

const convertYearsToDropdownOptions = (
  years: number[],
  labelFormatCb?: (year: number) => string
): YearDropdownOption[] =>
  years.map(
    (year: number): YearDropdownOption => ({
      label: _isUndefined(labelFormatCb) ? `${year}` : labelFormatCb(year),
      value: year
    })
  )

// prettier-ignore
const useOnDropdownChange = <T,>(
  setter: (value: T) => void
): ((item: DropdownOption) => void) =>
    useCallback(
      ({ value }): void => {
        setter(value as T)
      },
      [setter]
    )

const useDefaultNullState = (): [
  number | null,
  (value: number | null) => void
] => useState<number | null>(null)

type Props = {
  className?: string
  emissions: Emissions[]
}

export default function BaselineChartNew({ emissions, className }: Props) {
  const containerRef = useRef<any>(null)
  const { t } = useTranslation()
  const totalArea = useTotalArea(emissions)
  const years = getEmissionsYears(emissions)
  const mergedMonthlyData = useFunctionMemo<EmissionsData>(
    getEmissionsDataMerged,
    emissions
  )

  const [baselinePeriod, setBaselinePeriod] = useDefaultNullState()
  const [baselineEndYear, setBaselineEndYear] = useDefaultNullState()
  const [baselineStartYear, setBaselineStartYear] = useDefaultNullState()
  const displayModeOptions = useMemo(
    () => [
      {
        label: t('baseline_chart.display_mode_overlay'),
        value: DisplayMode.Overlay
      },
      {
        label: t('baseline_chart.display_mode_excess'),
        value: DisplayMode.Excess
      }
    ],
    [t]
  )

  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    DisplayMode.Overlay
  )

  const exportFileName = t('export.file_name')
  const exportNameColumnLabel = t('export.column_name')
  const exportNEEColumnLabel = t('export.column_nee')
  const exportColumnLabels = useMemo(
    (): string[] => [exportNameColumnLabel, exportNEEColumnLabel],
    [exportNameColumnLabel, exportNEEColumnLabel]
  )

  const yearIsWithinBaseline = useCallback(
    (year: number | string): boolean =>
      baselineStartYear !== null &&
      baselineEndYear !== null &&
      +year >= baselineStartYear &&
      +year <= baselineEndYear,
    [baselineStartYear, baselineEndYear]
  )

  const [interventionEndYear, setInterventionEndYear] = useDefaultNullState()
  const [interventionStartYear, setInterventionStartYear] =
    useDefaultNullState()

  // Forcefully set intervention start & end years if they are no longer valid
  // within the available years array. This can happen when the user updates
  // the start and end year filters externally.
  useEffect(() => {
    if (interventionStartYear === null || interventionEndYear === null) {
      return
    }

    const minYear = _min(years) ?? null
    const maxYear = _max(years) ?? null

    if (minYear !== null && interventionStartYear < minYear + 1) {
      setInterventionStartYear(minYear + 1)
    } else if (maxYear !== null && interventionStartYear > maxYear) {
      setInterventionStartYear(maxYear - 1)
    }

    if (minYear !== null && interventionEndYear < minYear) {
      setInterventionEndYear(minYear)
    } else if (maxYear !== null && interventionEndYear > maxYear) {
      setInterventionEndYear(maxYear)
    }
  }, [years, interventionStartYear, interventionEndYear])

  const yearIsWithinIntervention = useCallback(
    (year: number | string): boolean =>
      interventionStartYear !== null &&
      interventionEndYear !== null &&
      +year >= interventionStartYear &&
      +year <= interventionEndYear,
    [interventionStartYear, interventionEndYear]
  )

  const onSetInterventionStartYear = useOnDropdownChange<number>(
    setInterventionStartYear
  )

  const onSetInterventionEndYear = useOnDropdownChange<number>(
    setInterventionEndYear
  )

  const onSetBaselinePeriod = useOnDropdownChange<number>(setBaselinePeriod)

  const baselinePeriodOptions = useMemo(
    () =>
      convertYearsToDropdownOptions(
        getBaselinePeriodOptions(years, interventionStartYear),
        (year: number): string =>
          `${year}-yr ${t('baseline_chart.baseline_period_average_suffix')}`
      ),
    [years, interventionStartYear]
  )

  useEffect(() => {
    if (baselinePeriodOptions.length > 0 && baselinePeriod === null) {
      setBaselinePeriod(baselinePeriodOptions[0].value)
    }
  }, [baselinePeriodOptions])

  useEffect(() => {
    setBaselineEndYear(
      interventionStartYear === null ? null : interventionStartYear - 1
    )
  }, [interventionStartYear])

  useEffect(() => {
    setBaselineStartYear(
      interventionStartYear === null || baselinePeriod === null
        ? null
        : interventionStartYear - baselinePeriod - 1
    )
  }, [baselinePeriod, interventionStartYear])

  const dataPointCount = _values(mergedMonthlyData).length
  const totalNEE = useFunctionMemo<number>(
    getEmissionsDataTotal,
    mergedMonthlyData
  )

  const avgNEE = dataPointCount === 0 ? 0 : totalNEE / dataPointCount
  const initialUnit = avgNEE > 1000 ? EmissionsUnit.KTons : EmissionsUnit.Tons
  const [unit, setUnit] = useState<EmissionsUnit>(initialUnit)

  const onSetUnit = useOnDropdownChange<EmissionsUnit>(setUnit)

  const yearlyData = useMemo(
    (): EmissionsData => convertMonthlyDataToYearly(mergedMonthlyData),
    [mergedMonthlyData]
  )

  const avgBaseline = useMemo((): number => {
    if (baselineStartYear === null || baselineEndYear === null) {
      return 0
    }

    const yearlyNEE: EmissionsData = {}

    _keys(yearlyData)
      .filter(yearIsWithinBaseline)
      .forEach((year: string): void => {
        yearlyNEE[year] = yearlyData[year]
      })

    return _mean(_values(yearlyNEE))
  }, [yearlyData, baselineStartYear, baselineEndYear])

  const avgBaselineInUnit = useMemo(
    () => convertCO2TonsToUnit(avgBaseline, unit, totalArea),
    [avgBaseline, unit, totalArea]
  )

  // prettier-ignore
  const chartDataNEETCO2: ChartDataPoint[] = displayMode === DisplayMode.Overlay
    ? _keys(yearlyData)
      .filter(yearIsWithinIntervention)
      .map(
        (year: string): ChartDataPoint => ({
          x: new Date(`${year}`),
          y: yearlyData[year]
        })
      )
    : _keys(yearlyData)
      .filter(yearIsWithinIntervention)
      .map(
        (year: string): ChartDataPoint => ({
          x: new Date(`${year}`),
          y: yearlyData[year] - avgBaseline
        })
      )

  // prettier-ignore
  const additionalFluxInProjectPeriodtCO2 = useMemo(
    (): number =>
      interventionStartYear === null || interventionEndYear === null
        ? 0
        : _sum(
          _keys(yearlyData)
            .filter(yearIsWithinIntervention)
            .map((year: string): number => yearlyData[year] - avgBaseline)
        ),
    [yearlyData, interventionStartYear, interventionEndYear, avgBaseline]
  )

  const additionFluxInLastYeartCO2 = useMemo(() => {
    if (interventionEndYear === null) {
      return 0
    }

    const totalForYear = yearlyData[`${interventionEndYear}`]

    return _isUndefined(totalForYear) ? 0 : totalForYear - avgBaseline
  }, [yearlyData, interventionEndYear, avgBaseline])

  const chartDataInUnit: ChartDataPoint[] = useMemo(
    () =>
      chartDataNEETCO2.map(({ x, y }) => ({
        x,
        y: convertCO2TonsToUnit(y, unit, totalArea)
      })),
    [chartDataNEETCO2, unit, totalArea]
  )

  const additionalFluxInProjectPeriodInUnit = useMemo(
    () =>
      convertCO2TonsToUnit(additionalFluxInProjectPeriodtCO2, unit, totalArea),
    [additionalFluxInProjectPeriodtCO2, unit, totalArea]
  )

  const additionFluxInLastYearInUnit = useMemo(
    () => convertCO2TonsToUnit(additionFluxInLastYeartCO2, unit, totalArea),
    [additionFluxInLastYeartCO2, unit, totalArea]
  )

  const interventionStartYearOptions = getInterventionStartYearOptions(
    years,
    interventionEndYear
  )

  const interventionEndYearOptions = getInterventionEndYearOptions(
    years,
    interventionStartYear
  )

  // prettier-ignore
  const interventionStartYearOption = useMemo(
    (): DropdownOption | null =>
      interventionStartYear === -1
        ? null
        : {
            label: `${interventionStartYear ?? t('baseline_chart.select_start_year')}`,
            value: interventionStartYear
          },
    [interventionStartYear]
  )

  // prettier-ignore
  const interventionEndYearOption = useMemo(
    (): DropdownOption | null =>
      interventionEndYear === -1
        ? null
        : {
            label: `${interventionEndYear ?? t('baseline_chart.select_end_year')}`,
            value: interventionEndYear
          },
    [interventionEndYear]
  )

  const baselinePeriodOption = useMemo(
    (): DropdownOption | null =>
      baselinePeriodOptions.find(({ value }) => value === baselinePeriod) ??
      null,
    [baselinePeriodOptions, baselinePeriod]
  )

  const unitOption = useMemo(
    (): DropdownOption | null =>
      UNIT_OPTIONS.find(({ value }) => value === unit) ?? null,
    [UNIT_OPTIONS, unit]
  )

  const showBaseline =
    displayMode === DisplayMode.Overlay && !_isNull(baselinePeriod)
  const baselineValue = convertCO2TonsToUnit(avgBaseline, unit, totalArea)

  const onExportCSV = useCallback((): void => {
    const csv = generateChartCSV({
      data: chartDataInUnit,
      emissions,
      columnLabels: exportColumnLabels,
      chartTitle: t('baseline_chart.chart_title'),
      t
    })
    downloadCSV(csv, exportFileName)
  }, [emissions, exportColumnLabels, exportFileName, chartDataInUnit, t])

  const onExportPNG = useCallback(() => {
    const { current } = containerRef
    // eslint-disable-next-line github/no-then,@typescript-eslint/no-unsafe-argument
    exportElementToPNG(current, 400, 'result.png').catch((err: Error) => {
      console.error(err)
    })
  }, [containerRef.current])

  const onExportJPG = useCallback(() => {
    const { current } = containerRef
    // eslint-disable-next-line github/no-then,@typescript-eslint/no-unsafe-argument
    exportElementToJPG(current, 400, 'result.jpg').catch((err: Error) => {
      console.error(err)
    })
  }, [containerRef.current])

  // prettier-ignore
  return (
    <div className={cn(CLASS_NAME, className)}>
      <div className={`${CLASS_NAME}-right`}>
        <div className={`${CLASS_NAME}-title`}>
          <Trans i18nKey="baseline_chart.title" />
        </div>
        <div className={`${CLASS_NAME}-controls`}>
          <div className={`${CLASS_NAME}-controls-left`}>
            <Dropdown
              value={interventionStartYearOption}
              onChange={onSetInterventionStartYear}
              options={interventionStartYearOptions}
              label={t('baseline_chart.intervention_start')}
              helpText={t('baseline_chart.intervention_start_help')}
            />
            <Dropdown
              value={interventionEndYearOption}
              onChange={onSetInterventionEndYear}
              options={interventionEndYearOptions}
              label={t('baseline_chart.intervention_end')}
              helpText={t('baseline_chart.intervention_end_help')}
            />
            <Dropdown
              value={baselinePeriodOption}
              onChange={onSetBaselinePeriod}
              options={baselinePeriodOptions}
              placeholder={t('dropdown_placeholder')}
              label={t('baseline_chart.baseline_period')}
              disabled={baselinePeriodOptions.length === 0}
              helpText={t('baseline_chart.baseline_period_help')}
            />
            <div className={`${CLASS_NAME}-controls-left-vertical-container`}>
              <Dropdown
                value={unitOption}
                onChange={onSetUnit}
                options={UNIT_OPTIONS}
                label={t('unit_dropdown.label')}
                placeholder={t('unit_dropdown.placeholder')}
              />
              <p>
                <Trans i18nKey="baseline_chart.display_mode_switch_label" />
              </p>
              <Switch
                value={displayMode}
                onChange={setDisplayMode}
                optionA={displayModeOptions[0]}
                optionB={displayModeOptions[1]}
              />
            </div>
          </div>
        </div>
        <div className={`${CLASS_NAME}-chart-wrapper`}>
          {(_isNull(interventionStartYear) || _isNull(interventionEndYear)) && (
            <div className={`${CLASS_NAME}-placeholder`}>
              <Trans i18nKey="baseline_chart.instructions" />
            </div>
          )}
          {!_isNull(interventionStartYear) && !_isNull(interventionEndYear) && (
            <>
              <ChartHeader
                title={t('baseline_chart.chart_title')}
                onExportCSV={onExportCSV}
                onExportPNG={onExportPNG}
                onExportJPG={onExportJPG}
              />
              <div ref={containerRef}>
                <BarChartNew
                  granularity={BarChartGranularity.ANNUAL}
                  data={chartDataInUnit}
                  labelX={t('baseline_chart.chart_label_x')}
                  labelY={t('baseline_chart.chart_label_y', { unit })}
                  {...(showBaseline ? { baseline: baselineValue } : {})}
                  unit={unit}
                />
              </div>
            </>
          )}
        </div>
        {!_isNull(interventionStartYear) && !_isNull(interventionEndYear) && (
          <div className={`${CLASS_NAME}-stats`}>
            <div className={`${CLASS_NAME}-stats-item`}>
              <div className={`${CLASS_NAME}-stats-item-labels`}>
                <p>
                  <Trans i18nKey="baseline_chart.stat_average_baseline_title" />
                </p>
                <p>
                  <Trans i18nKey="baseline_chart.stat_average_baseline_description" />
                </p>
              </div>
              <div
                className={cn(`${CLASS_NAME}-stats-item-value`, {
                  red: avgBaselineInUnit > 0
                })}
              >
                <p>
                  {formatUINumber(avgBaselineInUnit)} {unit}
                </p>
              </div>
            </div>
            <div className={`${CLASS_NAME}-stats-item`}>
              <div className={`${CLASS_NAME}-stats-item-labels`}>
                <p>
                  <Trans i18nKey="baseline_chart.stat_additional_flux_over_project_period_title" />
                </p>
                <p>
                  <Trans i18nKey="baseline_chart.stat_additional_flux_over_project_period_description" />
                </p>
              </div>
              <div
                className={cn(`${CLASS_NAME}-stats-item-value`, {
                  red: additionalFluxInProjectPeriodInUnit > 0
                })}
              >
                <p>
                  {formatUINumber(additionalFluxInProjectPeriodInUnit)} {unit}
                </p>
              </div>
            </div>
            <div className={`${CLASS_NAME}-stats-item`}>
              <div className={`${CLASS_NAME}-stats-item-labels`}>
                <p>
                  <Trans i18nKey="baseline_chart.stat_additional_flux_in_last_year_title" />
                </p>
                <p>
                  <Trans i18nKey="baseline_chart.stat_additional_flux_in_last_year_description" />
                </p>
              </div>
              <div
                className={cn(`${CLASS_NAME}-stats-item-value`, {
                  red: additionFluxInLastYearInUnit > 0
                })}
              >
                <p>
                  {formatUINumber(additionFluxInLastYearInUnit)} {unit}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export { CLASS_NAME }
